import {Card} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {DailyStockTakeItem, DailyStockTakeMoney} from '../utils/buildDailyStockTakeDetailView';
import {DailyStockTakeBreakdown} from './DailyStockTakeBreakdown';
import {DailyStockTakeSummaryTile} from './DailyStockTakeSummaryTile';

export interface DailyStockTakeSummary {
  label: string;
  value: string;
  note?: string | Nullish;
}

interface DailyStockTakeMovementsProps extends RequiredTestIdProps {
  /** The two figures the day is read from, left to right. */
  inputs: [DailyStockTakeSummary, DailyStockTakeSummary];
  /**
   * What the two inputs add up to — the tile that carries the accent. Left out on a tab where
   * one of the two inputs is unknown, since the result would then only restate the other.
   */
  result?: DailyStockTakeSummary;
  receiptItems: DailyStockTakeItem[];
  receiptTotalLabel: string;
  receiptTotal: DailyStockTakeMoney | null;
  receiptArticles?: number | Nullish;
  issueItems: DailyStockTakeItem[];
  issueTotalLabel: string;
  issueTotal: DailyStockTakeMoney | null;
  issueArticles?: number | Nullish;
  correctionTotalLabel: string;
  correctionTotal: DailyStockTakeMoney | null;
  correctionDocuments?: number | Nullish;
}

/**
 * One tab of the daily closing: three figures on top, then receipts, issues and the check
 * side by side. Both tabs share this shape — only the numbers and the labels differ.
 */
export function DailyStockTakeMovements(props: DailyStockTakeMovementsProps) {
  return (
    <VStack spacing={2}>
      <Grid columns={isNotNil(props.result) ? [1, 3] : [1, 2]} spacing={2}>
        <DailyStockTakeSummaryTile
          label={props.inputs[0].label}
          value={props.inputs[0].value}
          note={props.inputs[0].note}
          data-testid={suffixTestId('opening', props)}
        />
        <DailyStockTakeSummaryTile
          label={props.inputs[1].label}
          value={props.inputs[1].value}
          note={props.inputs[1].note}
          data-testid={suffixTestId('closing', props)}
        />
        <Show when={isNotNil(props.result)}>
          <DailyStockTakeSummaryTile
            isHighlighted
            label={props.result?.label ?? ''}
            value={props.result?.value ?? EMPTY_PLACEHOLDER}
            note={props.result?.note}
            data-testid={suffixTestId('difference', props)}
          />
        </Show>
      </Grid>

      <Grid columns={[1, 3]} spacing={2}>
        <Card title={i18n.t('entity.warehouse.reporting.receiptsBreakdown')} colorStripe="green">
          <DailyStockTakeBreakdown
            items={props.receiptItems}
            totalLabel={props.receiptTotalLabel}
            total={props.receiptTotal}
            totalArticles={props.receiptArticles}
            data-testid={suffixTestId('receipts', props)}
          />
        </Card>
        <Card title={i18n.t('entity.warehouse.reporting.issuesBreakdown')} colorStripe="red">
          <DailyStockTakeBreakdown
            items={props.issueItems}
            totalLabel={props.issueTotalLabel}
            total={props.issueTotal}
            totalArticles={props.issueArticles}
            data-testid={suffixTestId('issues', props)}
          />
        </Card>
        <Card
          title={i18n.t('entity.warehouse.reporting.correctionsAndAdjustments')}
          colorStripe="blue"
        >
          {/*
            No lines: the closing job reports the day's corrections as one figure and exposes
            none of the parts behind it, so anything above the rule would have to be invented.
          */}
          <DailyStockTakeBreakdown
            items={[]}
            totalLabel={props.correctionTotalLabel}
            total={props.correctionTotal}
            totalDocuments={props.correctionDocuments}
            data-testid={suffixTestId('corrections', props)}
          />
        </Card>
      </Grid>
    </VStack>
  );
}
